import type { GeoPointJSON } from '@ikegps/core-api';
import { action, observable, makeObservable } from 'mobx';
import { T, Translate } from '../i18n';
import { type IkeClientService, Services } from './index';

/**
 * https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 */
const ADDRESS_TYPES = {
	CITY: 'locality',
	STATE: 'administrative_area_level_1',
};

export interface ReverseGeocodingAddress {
	address: string | null;
	city: string | null;
	state: string | null;
}

export class GeocoderService implements IkeClientService {
	__priority = 100;

	private geocoder: google.maps.Geocoder;

	@observable isReady = false;

	/** Promise for when google geocoder is ready */
	isReadyPromise: Promise<void>;

	constructor() {
		makeObservable(this);
	}

	init() {
		this.isReadyPromise = new Promise((resolve) => {
			const waitForGoogleGeocoder = Services.Window.window.setInterval(() => {
				if (typeof google === 'undefined') {
					return;
				}
				Services.Window.window.clearTimeout(waitForGoogleGeocoder);
				this.setIsReady();
				resolve();
			}, 10);
		});
	}

	@action setIsReady() {
		this.isReady = true;
	}

	getLatLng(location: GeoPointJSON): google.maps.LatLng {
		const latLng = new google.maps.LatLng(location.coordinates[1], location.coordinates[0]);

		return latLng;
	}

	getGeocoderRequest(location: google.maps.LatLng): google.maps.GeocoderRequest {
		const geocoderRequest = {
			location,
			language: 'en',
		};

		return geocoderRequest;
	}

	private geocoderResult(location: GeoPointJSON): Promise<google.maps.GeocoderResult> {
		return new Promise((resolve, reject) => {
			if (this.geocoder == null) {
				this.geocoder = new google.maps.Geocoder();
			}
			const latLng = this.getLatLng(location);
			const geocoderRequest = this.getGeocoderRequest(latLng);
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.geocoder.geocode(geocoderRequest, (results, status) => {
				if (status.toString() === 'OK') {
					resolve(results[0]);
				} else {
					reject(status);
				}
			});
		});
	}

	async getAddress(location: GeoPointJSON): Promise<any> {
		if (!this.isReady) {
			throw new Error('Cannot fetch geocode before Google Maps API loaded');
		}

		let result: ReverseGeocodingAddress = {
			address: null,
			city: null,
			state: null,
		};

		try {
			const geoCoderResult = await this.geocoderResult(location);

			Services.Log.Logger.info(geoCoderResult.formatted_address);
			result.address = geoCoderResult.formatted_address;

			for (const component of geoCoderResult.address_components) {
				if (component.types.indexOf(ADDRESS_TYPES.CITY) > -1) {
					result.city = component.long_name;
				}

				if (component.types.indexOf(ADDRESS_TYPES.STATE) > -1) {
					result.state = component.long_name;
				}
			}

			return result;
		} catch (err) {
			Services.Log.Logger.error(err);
			result.address = Translate(T.ERROR_GENERAL);
			return result;
		}
	}
}
